import React, { useEffect, useState } from 'react';
import {
  Box,
  Divider,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  ButtonBase,
  Tooltip,
  Chip,
  Skeleton,
} from '@mui/material';
import {
  Delete,
  Add,
  Remove,
  WorkOutline,
  Close,
  Construction,
  Build,
  Edit,
} from '@mui/icons-material';
import withComponentAuth from '../../hoc/withComponentAuth';
import {
  ITool,
  IToolBag,
  IToolBagRequest,
} from '../../utils/entities/interface';
import {
  DeleteTool,
  DeleteToolBag,
  GetToolBags,
  GetToolBagsRequests,
  GetTools,
  ModifyToolBag,
} from '../../services/tool';
import AddToolModal from '../../components/ui/add/toolmodal';
import AddEditToolBagModal from '../../components/ui/add/toolbagmodal';
import TiTModal from '../../components/ui/add/titmodal';
import useSnackbar from '../../hooks/useSnackbar';
import { formatDateAndTime } from '../../utils/helper';
import AssignToolBagModal from './Modals/AssignToolBag';

// Define ToolBagRequest component FIRST
const ToolBagRequest = () => {
  const { SnackbarComponent, showSnackbar } = useSnackbar();
  const [requests, setRequests] = useState<IToolBagRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await GetToolBagsRequests();
      // Ensure response is an array
      setRequests(Array.isArray(response) ? response : []);
    } catch (error: any) {
      console.error('Error fetching tool bag requests:', error);
      showSnackbar('Failed to load tool bag requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = (id: string) => {
    setSelectedRequestId(id);
  };

  const handleModalClose = () => {
    setSelectedRequestId(null);
  };

  const handleModalSuccess = () => {
    fetchRequests();
    handleModalClose();
  };

  return (
    <>
      {SnackbarComponent}
      <Paper
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          scrollbarWidth: 'none',
          p: 2,
          backgroundColor: 'background.default',
        }}
      >
        <Typography variant="h6" fontWeight="bold" mb={2}>
          Tool Bag Requests
        </Typography>
        {loading ? (
          Array.from({ length: 4 }).map((_, idx) => (
            <Box key={idx} mb={2}>
              <Skeleton
                variant="rectangular"
                height={100}
                sx={{ borderRadius: 2 }}
              />
            </Box>
          ))
        ) : (
          <List disablePadding>
            {requests.map((req) => (
              <Paper
                key={req._id}
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 1px 5px rgba(0,0,0,0.08)',
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={9}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={0.5}>
                      {req.technician?.name || 'Technician Name'}
                    </Typography>

                    <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                      <Chip
                        label={`Status: ${req.status}`}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={req.isAssigned ? 'Assigned' : 'Not Assigned'}
                        color={req.isAssigned ? 'success' : 'warning'}
                        size="small"
                        variant="outlined"
                      />
                      {req.assignedToolBag && (
                        <Chip
                          label={`Bag: ${req.assignedToolBag.name}`}
                          color="secondary"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      Requested on {formatDateAndTime(req.createdAt)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Box
                      display="flex"
                      justifyContent={{ xs: 'center', sm: 'flex-end' }}
                      alignItems="center"
                      height="100%"
                    >
                      {!req.isAssigned ? (
                        <Button
                          variant="contained"
                          size="small"
                          fullWidth={true}
                          sx={{ maxWidth: { xs: '100%', sm: 'auto' } }}
                          onClick={() => handleAssign(req._id)}
                        >
                          Assign
                        </Button>
                      ) : (
                        <Typography
                          variant="body2"
                          color="success.main"
                          textAlign={{ xs: 'center', sm: 'right' }}
                          width="100%"
                        >
                          ✔ Already Assigned
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </List>
        )}
        {selectedRequestId && (
          <AssignToolBagModal
            isOpen={Boolean(selectedRequestId)}
            onClose={handleModalClose}
            requestId={selectedRequestId}
            onReload={handleModalSuccess}
          />
        )}
      </Paper>
    </>
  );
};

// Define ToolInventory component
const ToolInventory = ({
  handleToolAdd,
}: {
  handleToolAdd: (tool: ITool) => void;
}) => {
  const [allTools, setAllTools] = useState<ITool[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const { SnackbarComponent, showSnackbar, resetSnackbar } = useSnackbar();

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      const response = await GetTools({});
      // Ensure data is an array
      setAllTools(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching tools:', error);
    }
  };

  const handleAddClose = () => {
    setOpenModal(false);
  };

  const handleSaved = () => {
    setOpenModal(false);
    fetchTools();
  };

  const handleRemoveFromToolList = async (toolId: string) => {
    try {
      const status = await DeleteTool(toolId);
      if (status == 200) {
        showSnackbar('Tool removed from inventory', 'success');
        fetchTools();
        setTimeout(() => {
          resetSnackbar();
        }, 1000);
      }
    } catch (error) {
      console.error('Error removing tool from list:', error);
      showSnackbar('Failed to remove tool from inventory', 'error');
      resetSnackbar();
    }
  };

  return (
    <>
      {SnackbarComponent}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 10,
          p: 2,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 'bold', letterSpacing: 0.5 }}
        >
          Tool Inventory
        </Typography>
        <Button
          variant="contained"
          size="small"
          sx={{ m: 0.5 }}
          onClick={() => setOpenModal(true)}
          startIcon={<Add />}
        >
          Add New Tool
        </Button>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          padding: 1,
        }}
      >
        {allTools.map((tool: ITool) => (
          <Button
            key={tool._id}
            variant="outlined"
            size="small"
            sx={{ m: 0.5 }}
            onClick={() => handleToolAdd(tool)}
            endIcon={
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFromToolList(tool._id);
                }}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <Close />
              </span>
            }
          >
            {tool.name}
          </Button>
        ))}
      </Box>
      <AddToolModal
        open={openModal}
        onClose={handleAddClose}
        onSaved={handleSaved}
      />
    </>
  );
};

// Main ToolManagement component
const ToolManagement = () => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [selectedBag, setSelectedBag] = useState<IToolBag | null>(null);
  const [bags, setBags] = useState<IToolBag[] | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const { SnackbarComponent, showSnackbar, resetSnackbar } = useSnackbar();
  const [titmodal, setTitmodal] = useState<{
    tool: ITool | null;
    toolbag: IToolBag | null;
    open: boolean;
  }>({
    tool: null,
    toolbag: null,
    open: false,
  });

  useEffect(() => {
    fetchToolBags();
  }, []);

  const fetchToolBags = async () => {
    try {
      setLoading(true);
      const response = await GetToolBags({ page: 1, limit: 1000 });
      // Ensure data is an array
      setBags(Array.isArray(response?.data) ? response.data : []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching tool bags:', err);
      setLoading(false);
    }
  };

  const handleToolAdd = async (tool: ITool) => {
    if (!selectedBag) return;
    if (!selectedBag._id) return;
    if (!tool._id) return;
    setTitmodal({
      tool: tool,
      toolbag: selectedBag,
      open: true,
    });
  };

  const handleToolRemove = async (toolId: string) => {
    if (!selectedBag) return;
    if (!selectedBag._id) return;

    try {
      const status = await ModifyToolBag({
        toolBagId: selectedBag._id,
        toolId: toolId,
        action: 'remove',
      });
      if (status === 200) {
        showSnackbar('Tool removed from Toolbag successfully', 'success');
        fetchToolBags();
        setTimeout(() => {
          resetSnackbar();
        }, 1000);
      }
    } catch (err) {
      console.error('Error removing tool from bag:', err);
    }
  };

  const handleAddClose = () => {
    setOpenModal(false);
    setSelectedBag(null);
  };

  const handleSaved = () => {
    setOpenModal(false);
    setSelectedBag(null);
    fetchToolBags();
  };

  const handletitClose = () => {
    setTitmodal({
      tool: null,
      toolbag: null,
      open: false,
    });
  };

  const handletitSaved = () => {
    setTitmodal({
      tool: null,
      toolbag: null,
      open: false,
    });
    fetchToolBags();
  };

  const handledeleteBag = async (toolBagId: string) => {
    try {
      const status = await DeleteToolBag(toolBagId);
      if (status === 200) {
        showSnackbar('Tool bag deleted successfully', 'success');
        fetchToolBags();
        setTimeout(() => {
          resetSnackbar();
        }, 1000);
      }
    } catch (err) {
      console.error('Error deleting tool bag:', err);
      showSnackbar('Failed to delete tool bag', 'error');
      resetSnackbar();
    }
  };

  const selected =
    Array.isArray(bags) && bags.find((b) => b._id === selectedBag?._id);

  return (
    <Box
      sx={{ p: 2, height: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <Grid container spacing={2} sx={{ flexGrow: 1, height: '100%' }}>
        {/* ToolBag List */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {SnackbarComponent}
          <Paper
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              scrollbarWidth: 'none',
            }}
          >
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'background.paper',
                zIndex: 10,
                p: 2,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 'bold', letterSpacing: 0.5 }}
              >
                Tool Bags
              </Typography>

              <Button
                variant="contained"
                startIcon={<WorkOutline />}
                size="small"
                onClick={() => setOpenModal(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  borderRadius: 2,
                }}
              >
                New Bag
              </Button>
            </Box>

            {Array.isArray(bags) && bags.length > 0 ? (
              <List sx={{ p: 1, m: 0 }}>
                {bags.map((bag) => (
                  <ListItem
                    key={bag._id}
                    sx={{
                      borderRadius: 2,
                      boxShadow: 1,
                      mb: 1,
                      px: 2,
                      py: 1.5,
                      bgcolor: 'background.paper',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: 'primary.light',
                          color: 'primary.dark',
                        }}
                      >
                        {bag.image ? (
                          <img
                            src={bag.image}
                            alt={bag.name}
                            style={{
                              width: 30,
                              height: 30,
                              objectFit: 'contain',
                            }}
                          />
                        ) : (
                          <Build />
                        )}
                      </Avatar>
                    </ListItemAvatar>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        pl: 1,
                      }}
                    >
                      <ButtonBase
                        onClick={() => setSelectedBag(bag)}
                        sx={{
                          textAlign: 'left',
                          width: '100%',
                          borderRadius: 1,
                          justifyContent: 'flex-start',
                          alignItems: 'flex-start',
                        }}
                      >
                        <ListItemText
                          primary={
                            <>
                              <Tooltip title={bag.description || ''} arrow>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  {bag.name}
                                </Typography>
                              </Tooltip>
                            </>
                          }
                        />
                      </ButtonBase>

                      <Box sx={{ display: 'flex', flexShrink: 0, ml: 1 }}>
                        <Tooltip title="Edit Tool Bag">
                          <IconButton
                            edge="end"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBag(bag);
                              setOpenModal(true);
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Tool Bag">
                          <IconButton
                            edge="end"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              bag && bag._id && handledeleteBag(bag._id);
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box
                sx={{
                  mt: 4,
                  textAlign: 'center',
                  color: 'text.secondary',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <Construction sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                  No tool bags found
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Create a new tool bag to get started.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Selected ToolBag Tools */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Paper
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              scrollbarWidth: 'none',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'background.paper',
                zIndex: 10,
                p: 2,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 'bold', letterSpacing: 0.5 }}
              >
                {selected ? `Tools in ${selected.name}` : 'Select a ToolBag'}
              </Typography>
            </Box>

            <Box sx={{ p: 0 }}>
              {selected ? (
                <>
                  {selected.tools.length === 0 ? (
                    <Box
                      sx={{
                        mt: 4,
                        textAlign: 'center',
                        color: 'text.secondary',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      <Construction
                        sx={{ fontSize: 48, color: 'grey.500', mb: 1 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 'medium', mb: 0.5 }}
                      >
                        No tools in this bag
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        Tap below to get started.
                      </Typography>
                    </Box>
                  ) : (
                    <List>
                      {selected.tools.map((tool) => (
                        <ListItem key={tool.toolId}>
                          <Tooltip title={tool.description || ''} arrow>
                            <ListItemText
                              primary={`${tool.name} × ${tool.quantity}`}
                            />
                          </Tooltip>

                          <IconButton
                            color="error"
                            onClick={() =>
                              tool &&
                              tool.toolId &&
                              handleToolRemove(tool.toolId)
                            }
                            aria-label="remove tool"
                          >
                            <Remove />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                  )}

                  <Divider sx={{ my: 0 }} />
                  <ToolInventory handleToolAdd={handleToolAdd} />
                  <TiTModal
                    open={titmodal.open}
                    toolBag={titmodal.toolbag}
                    tool={titmodal.tool}
                    onClose={handletitClose}
                    onSaved={handletitSaved}
                  />
                </>
              ) : null}
            </Box>
          </Paper>
        </Grid>

        {/* Tool Requests */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ToolBagRequest />
        </Grid>
      </Grid>

      <AddEditToolBagModal
        open={openModal}
        onClose={handleAddClose}
        onSaved={handleSaved}
        toolbag={selectedBag}
      />
    </Box>
  );
};

export default withComponentAuth(ToolManagement);
